import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Shield, ShieldCheck, ShieldX } from 'lucide-react';
import { emailService } from '../../services';
import { messageHandler } from '../../services/message-handler.service';
import type { WhiteBlackListEntry } from '../../types/email';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  EmptyState,
  Modal,
  Form,
  FormField,
  FormLabel,
  FormInput,
  FormSelect,
  Badge,
} from '../ui';

function WhiteBlackListManager() {
  const [modal, setModal] = useState<{ type: 'create' | 'edit' | ''; data?: WhiteBlackListEntry }>({
    type: '',
  });
  const queryClient = useQueryClient();

  const { data: entries, isLoading } = useQuery({
    queryKey: ['white-black-list'],
    queryFn: () => emailService.getWhiteBlackList(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<WhiteBlackListEntry, 'id' | 'created_at'>) =>
      emailService.addToList(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['white-black-list'] });
      messageHandler.success('Email adicionado à lista com sucesso');
      setModal({ type: '' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'whitelist' | 'blacklist' }) =>
      emailService.updateListEntry(id, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['white-black-list'] });
      messageHandler.success('Lista atualizada com sucesso');
      setModal({ type: '' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => emailService.deleteListEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['white-black-list'] });
      messageHandler.success('Email removido da lista com sucesso');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      email: formData.get('email') as string,
      action: formData.get('action') as 'whitelist' | 'blacklist',
    };

    if (modal.type === 'edit' && modal.data) {
      updateMutation.mutate({ id: modal.data.id, action: data.action });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (entry: WhiteBlackListEntry) => {
    messageHandler.confirm(
      'Remover da Lista',
      'Tem certeza que deseja remover este email da lista?',
      () => deleteMutation.mutate(entry.id)
    );
  };

  const getActionBadge = (action: string) => {
    if (action === 'whitelist') {
      return (
        <Badge variant="success" className="flex items-center">
          <ShieldCheck className="h-4 w-4 mr-1" />
          Whitelist
        </Badge>
      );
    }
    return (
      <Badge variant="error" className="flex items-center">
        <ShieldX className="h-4 w-4 mr-1" />
        Blacklist
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        <Button onClick={() => setModal({ type: 'create' })}>
          <Plus className="h-4 w-4 mr-2" />
          Adicionar Email
        </Button>
      </div>

      {entries?.length > 0 ? (
        <TableContainer>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Lista</TableHead>
                <TableHead>Data de Criação</TableHead>
                <TableHead className="relative">
                  <span className="sr-only">Ações</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map(entry => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.email}</TableCell>
                  <TableCell>{getActionBadge(entry.action)}</TableCell>
                  <TableCell>{new Date(entry.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setModal({ type: 'edit', data: entry })}
                        title="Editar"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(entry)}
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState
          icon={<Shield className="h-12 w-12" />}
          title="Nenhum email na lista"
          description="Adicione emails à whitelist ou blacklist para começar."
          action={{
            label: 'Adicionar Email',
            onClick: () => setModal({ type: 'create' }),
          }}
        />
      )}

      <Modal
        isOpen={modal.type !== ''}
        onClose={() => setModal({ type: '' })}
        title={modal.type === 'create' ? 'Adicionar Email' : 'Editar Lista'}
        size="md"
      >
        <Form onSubmit={handleSubmit}>
          {modal.type === 'create' && (
            <FormField>
              <FormLabel htmlFor="email" required>
                Email
              </FormLabel>
              <FormInput type="email" id="email" name="email" required />
            </FormField>
          )}

          <FormField>
            <FormLabel htmlFor="action" required>
              Lista
            </FormLabel>
            <FormSelect id="action" name="action" defaultValue={modal.data?.action} required>
              <option value="whitelist">Whitelist</option>
              <option value="blacklist">Blacklist</option>
            </FormSelect>
          </FormField>

          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
            <Button
              type="submit"
              isLoading={createMutation.isPending || updateMutation.isPending}
              className="sm:col-start-2"
            >
              {modal.type === 'create' ? 'Adicionar' : 'Salvar'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setModal({ type: '' })}
              className="sm:col-start-1"
            >
              Cancelar
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

export default WhiteBlackListManager;
